import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TestModeService, TestOwnerRow } from '../../services/test-mode';
import { ToastService } from '../../services/toast';

/**
 * Test mode console: the switch, what it has produced so far, and the purge.
 *
 * The destructive action is deliberately awkward — a dialog that spells out the exact counts
 * and requires typing DELETE — because it is one click away from the same screen that toggles
 * the mode, and the two must never be confused.
 */
@Component({
  selector: 'app-test-mode',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, ToggleSwitchModule, DialogModule, TagModule],
  template: `
    <div class="page">
      <div class="page-head">
        <div>
          <h2>Test Mode</h2>
          <p>Try the whole system end to end without touching real data.</p>
        </div>
      </div>

      <!-- ============ the switch ============ -->
      <div class="card tm-card" [class.on]="svc.enabled()">
        <div class="tm-main">
          <div class="tm-icon"><i class="pi" [class.pi-wrench]="svc.enabled()" [class.pi-check-circle]="!svc.enabled()"></i></div>
          <div class="tm-text">
            <h3>
              {{ svc.enabled() ? 'Test mode is ON' : 'Test mode is OFF' }}
              <p-tag [value]="svc.enabled() ? 'TEST' : 'LIVE'" [severity]="svc.enabled() ? 'warn' : 'success'" />
            </h3>
            <p>
              @if (svc.enabled()) {
                New enrolments are marked as test data and numbered from a separate series
                (<code>TEST-2026-27-0000001</code>). They can be cleared at any time.
              } @else {
                New enrolments are live: real member numbers, real policy numbers, permanent records.
              }
            </p>
          </div>
        </div>
        <p-toggleswitch [ngModel]="svc.enabled()" (ngModelChange)="toggle($event)" [disabled]="saving()" />
      </div>

      <!-- ============ what exists right now ============ -->
      <div class="tiles">
        <div class="tile test">
          <span>Test members</span>
          <strong>{{ svc.status()?.testMembers ?? 0 }}</strong>
        </div>
        <div class="tile test">
          <span>Test payments</span>
          <strong>{{ svc.status()?.testPayments ?? 0 }}</strong>
        </div>
        <div class="tile live">
          <span>Live members</span>
          <strong>{{ svc.status()?.liveMembers ?? 0 }}</strong>
        </div>
        <div class="tile live">
          <span>Live payments</span>
          <strong>{{ svc.status()?.livePayments ?? 0 }}</strong>
        </div>
      </div>

      <!-- ============ per client / partner ============ -->
      @if (svc.status()?.byOwner?.length) {
        <div class="card owners-card">
          <h3>Test data by client / partner</h3>
          <p class="owners-lede">Clear one owner's test data without touching anyone else's.</p>
          <table class="owners">
            <thead>
              <tr><th>Owner</th><th>Type</th><th class="r">Members</th><th class="r">Payments</th><th></th></tr>
            </thead>
            <tbody>
              @for (o of svc.status()!.byOwner; track o.scope + '-' + o.ownerId) {
                <tr>
                  <td class="o-name">{{ o.name || '—' }}<span>{{ o.code }}</span></td>
                  <td><span class="chip" [class.partner]="o.scope === 'PARTNER'">{{ o.scope }}</span></td>
                  <td class="r">{{ o.members }}</td>
                  <td class="r">{{ o.payments }}</td>
                  <td class="r">
                    <p-button label="Clear" icon="pi pi-trash" severity="danger" [text]="true"
                      size="small" (onClick)="openConfirm(o)" />
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      <!-- ============ the purge ============ -->
      <div class="card danger-card">
        <div>
          <h3><i class="pi pi-trash"></i> Clear ALL test data</h3>
          <p>
            Deletes every test-marked member and payment across all clients and partners, along
            with their nominees, certificates and messages, and restarts test numbering at 0000001.
          </p>
          <ul class="safe-list">
            <li><i class="pi pi-check"></i> Live members and payments are never touched</li>
            <li><i class="pi pi-check"></i> Clients, partners, policies and premium rules are kept</li>
            <li><i class="pi pi-check"></i> Payment gateways, API keys and all settings are kept</li>
            <li><i class="pi pi-check"></i> The live member number counter does not move</li>
          </ul>
        </div>
        <p-button label="Clear all test data" icon="pi pi-trash" severity="danger" [outlined]="true"
          [disabled]="(svc.status()?.testMembers ?? 0) === 0 && (svc.status()?.testPayments ?? 0) === 0"
          (onClick)="openConfirm(null)" />
      </div>
    </div>

    <p-dialog [header]="target() ? 'Clear test data — ' + target()!.name : 'Clear ALL test data'"
      [visible]="confirming()" (visibleChange)="confirming.set($event)"
      [modal]="true" [draggable]="true" [style]="{ width: '520px' }">
      <p class="cf-lead">This will permanently delete:</p>
      <ul class="cf-list">
        @if (target(); as t) {
          <li><strong>{{ t.members }}</strong> test member(s) belonging to <b>{{ t.name }}</b></li>
          <li><strong>{{ t.payments }}</strong> of their test payment(s)</li>
          <li>Other clients' and partners' test data is <b>not</b> affected</li>
        } @else {
          <li><strong>{{ svc.status()?.testMembers ?? 0 }}</strong> test member(s) — every client and partner</li>
          <li><strong>{{ svc.status()?.testPayments ?? 0 }}</strong> test payment(s)</li>
        }
        <li>their nominees, generated certificates and queued messages</li>
      </ul>
      <p class="cf-safe">
        <i class="pi pi-shield"></i>
        {{ svc.status()?.liveMembers ?? 0 }} live member(s) and {{ svc.status()?.livePayments ?? 0 }}
        live payment(s) will not be touched, and no master data is affected.
      </p>
      <label class="cf-label">Type <b>DELETE</b> to confirm</label>
      <input class="cf-input" [(ngModel)]="confirmText" placeholder="DELETE" autocomplete="off" />

      <ng-template pTemplate="footer">
        <p-button label="Cancel" [text]="true" severity="secondary" (onClick)="confirming.set(false)" />
        <p-button label="Delete test data" icon="pi pi-trash" severity="danger"
          [loading]="clearing()" [disabled]="confirmText !== 'DELETE'" (onClick)="doReset()" />
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    :host { display: block; }
    .page-head h2 { font-size: 22px; font-weight: 700; margin: 0; color: var(--ink, #0f172a); }
    .page-head p { margin: 2px 0 16px; color: var(--muted, #6b7280); font-size: 14px; }
    .card { background: #fff; border-radius: 16px; box-shadow: 0 4px 14px rgba(15,23,42,.05); }

    .tm-card {
      display: flex; align-items: center; gap: 16px; justify-content: space-between;
      padding: 18px 20px; margin-bottom: 14px; border: 1px solid #eef2f6;
      border-left: 4px solid #16a34a;
    }
    .tm-card.on { border-left-color: #d97706; background: #fffbeb; }
    /* p-toggleswitch is a block-level custom element, so without explicit flex rules it took a
       full line of its own and dropped under the copy instead of sitting opposite it. */
    .tm-main { display: flex; gap: 14px; align-items: flex-start; min-width: 0; flex: 1 1 auto; }
    .tm-card > p-toggleswitch { flex: 0 0 auto; margin-left: auto; }
    .tm-icon {
      width: 42px; height: 42px; border-radius: 12px; flex: 0 0 auto;
      display: inline-flex; align-items: center; justify-content: center;
      background: #dcfce7; color: #15803d; font-size: 18px;
    }
    .tm-card.on .tm-icon { background: #fef3c7; color: #b45309; }
    .tm-text h3 { margin: 0 0 4px; font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
    .tm-text p { margin: 0; font-size: 13px; color: var(--muted, #6b7280); line-height: 1.5; max-width: 70ch; }
    code { background: rgba(0,0,0,.06); padding: 1px 5px; border-radius: 5px; font-size: 12px; }

    .tiles { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 12px; margin-bottom: 14px; }
    .tile {
      background: #fff; border: 1px solid #eef2f6; border-radius: 14px; padding: 14px 16px;
      display: flex; flex-direction: column; gap: 2px;
    }
    .tile span { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: var(--muted, #6b7280); }
    .tile strong { font-size: 24px; font-weight: 800; color: var(--ink, #0f172a); font-variant-numeric: tabular-nums; }
    .tile.test strong { color: #b45309; }
    .tile.live strong { color: #15803d; }

    .danger-card {
      display: flex; align-items: flex-start; justify-content: space-between; gap: 20px;
      padding: 18px 20px; border: 1px solid #fca5a5;
    }
    .danger-card h3 { margin: 0 0 4px; font-size: 15px; font-weight: 700; color: #991b1b; display: flex; align-items: center; gap: 8px; }
    .danger-card p { margin: 0 0 10px; font-size: 13px; color: var(--muted, #6b7280); max-width: 70ch; line-height: 1.5; }
    .safe-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
    .safe-list li { font-size: 12.5px; color: #15803d; display: flex; align-items: center; gap: 7px; }
    .safe-list i { font-size: 11px; }

    /* per-owner table */
    .owners-card { padding: 16px 18px; margin-bottom: 14px; border: 1px solid #eef2f6; }
    .owners-card h3 { margin: 0 0 2px; font-size: 15px; font-weight: 700; }
    .owners-lede { margin: 0 0 12px; font-size: 13px; color: var(--muted, #6b7280); }
    table.owners { width: 100%; border-collapse: collapse; }
    table.owners th {
      text-align: left; font-size: 11.5px; font-weight: 700; text-transform: uppercase;
      letter-spacing: .04em; color: var(--muted, #6b7280); padding: 6px 8px; border-bottom: 1px solid #eef2f6;
    }
    table.owners td { padding: 9px 8px; border-bottom: 1px solid #f5f8fa; font-size: 13px; vertical-align: middle; }
    table.owners tr:last-child td { border-bottom: 0; }
    table.owners .r { text-align: right; }
    .o-name { font-weight: 600; }
    .o-name span { display: block; font-weight: 400; font-size: 11.5px; color: var(--muted, #6b7280); }
    .chip {
      font-size: 10.5px; font-weight: 800; letter-spacing: .05em; padding: 2px 8px;
      border-radius: 999px; background: #e0f2fe; color: #075985;
    }
    .chip.partner { background: #ede9fe; color: #5b21b6; }

    .cf-lead { margin: 0 0 6px; font-size: 13.5px; }
    .cf-list { margin: 0 0 12px; padding-left: 18px; font-size: 13px; display: flex; flex-direction: column; gap: 3px; }
    .cf-safe {
      display: flex; gap: 8px; align-items: flex-start; font-size: 12.5px; color: #15803d;
      background: #f0fdf4; border: 1px solid #86efac; border-radius: 10px; padding: 9px 11px; margin: 0 0 14px;
    }
    .cf-label { font-size: 12.5px; font-weight: 600; display: block; margin-bottom: 5px; }
    .cf-input {
      width: 100%; height: 40px; border: 1.5px solid var(--line, #e7ecef); border-radius: 9px;
      padding: 0 11px; font-size: 14px; letter-spacing: .06em;
    }
    .cf-input:focus { outline: none; border-color: #dc2626; }

    @media (max-width: 900px) {
      .tiles { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .danger-card { flex-direction: column; }
      .tm-card { flex-direction: column; align-items: flex-start; }
    }
  `],
})
export class TestModePage implements OnInit {
  readonly svc = inject(TestModeService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  readonly saving = signal(false);
  readonly clearing = signal(false);
  readonly confirming = signal(false);
  /** Which owner the pending purge is scoped to; null means "everything". */
  readonly target = signal<TestOwnerRow | null>(null);
  confirmText = '';

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.svc.refresh();
  }

  toggle(on: boolean) {
    this.saving.set(true);
    this.svc.set(on).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not change test mode.');
          return;
        }
        this.svc.enabled.set(on);
        this.toast.success(res?.message || (on ? 'Test mode is ON.' : 'Test mode is OFF.'));
        this.svc.refresh();
      },
      error: () => {
        this.saving.set(false);
        this.toast.error('Could not change test mode.');
      },
    });
  }

  /** null = clear everything; a row = clear just that client's / partner's test data. */
  openConfirm(owner: TestOwnerRow | null) {
    this.target.set(owner);
    this.confirmText = '';
    this.confirming.set(true);
  }

  doReset() {
    if (this.confirmText !== 'DELETE') return;
    const t = this.target();
    const scope = t
      ? t.scope === 'CLIENT'
        ? { clientId: t.ownerId }
        : { partnerId: t.ownerId }
      : undefined;
    this.clearing.set(true);
    this.svc.reset(scope).subscribe({
      next: (res: any) => {
        this.clearing.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Could not clear the test data.');
          return;
        }
        this.confirming.set(false);
        this.toast.success(res?.message || 'Test data cleared.');
        this.svc.refresh();
      },
      error: () => {
        this.clearing.set(false);
        this.toast.error('Could not clear the test data.');
      },
    });
  }
}
